/**
 * Mobile Scroll Service for NLFX Cable Builder
 * Handles global scrolling behavior when embedded in iframes on mobile devices
 */

class MobileScrollService {
  constructor() {
    this.isEmbedded = false;
    this.isMobile = false;
    this.isTouchDevice = false;
    this.scrollThreshold = 10; // Minimum scroll distance to trigger parent scroll
    this.lastScrollY = 0;
    this.scrollTimeout = null;
    this.isScrolling = false;
    this.iframeScrollContinuation = true; // Enable scroll continuation
    this.scrollDirection = 'down'; // Track scroll direction
    this.iframeBottomReached = false;
    this.parentScrollOffset = 0; // Track how much we've scrolled the parent
    this.internalNavigationActive = false; // Track if user is navigating within app
    this.navigationTimeout = null; // Timeout to reset internal navigation mode
    
    this.init();
  }

  init() {
    this.detectEnvironment();
    
    if (this.isEmbedded && this.isMobile) {
      this.setupGlobalScrolling();
      this.addMobileScrollOptimizations();
      this.setupInternalNavigationDetection();
    }
  }

  detectEnvironment() {
    // Check if embedded via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasEmbedParam = urlParams.get('embed') === 'true';
    
    // Check if in iframe
    const isInIframe = window.self !== window.top;
    
    // Enhanced mobile device detection for all devices
    const userAgent = navigator.userAgent;
    const isIPad = /iPad/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(userAgent);
    const isIPhone = /iPhone|iPod/i.test(userAgent);
    const isMobileUA = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isSmallScreen = window.innerWidth <= 1024; // Increased to include tablets
    
    this.isMobile = isSmallScreen || isMobileUA || isIPad;
    
    // Enhanced touch device detection for all mobile devices
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || isIPad || isAndroid || isIPhone;
    
    // Check referrer for external embedding
    const referrer = document.referrer;
    const currentDomain = window.location.hostname;
    const referrerDomain = referrer ? new URL(referrer).hostname : null;
    const isFromDifferentDomain = referrerDomain && referrerDomain !== currentDomain;
    
    this.isEmbedded = hasEmbedParam || isInIframe || isFromDifferentDomain;
    
    console.log('🔍 [MOBILE-SCROLL] Environment detected:', {
      isEmbedded: this.isEmbedded,
      isMobile: this.isMobile,
      isTouchDevice: this.isTouchDevice,
      isIPad,
      isAndroid,
      isIPhone,
      isInIframe,
      hasEmbedParam,
      isFromDifferentDomain,
      userAgent: userAgent,
      screenWidth: window.innerWidth,
      maxTouchPoints: navigator.maxTouchPoints
    });
  }

  setupGlobalScrolling() {
    console.log('📱 [MOBILE-SCROLL] Setting up global scrolling for embedded mobile app');
    
    // Add scroll event listeners with better iPad support
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    // passive: true so we never block or delay the browser's touch scroll inside the iframe
    window.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: true });
    window.addEventListener('wheel', this.handleWheel.bind(this), { passive: true });
    
    // Add additional iPad-specific events
    if (this.isTouchDevice) {
      window.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      window.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    }
    
    // Add CSS to optimize scrolling
    this.addScrollOptimizations();
  }

  setupInternalNavigationDetection() {
    console.log('🔄 [MOBILE-SCROLL] Setting up internal navigation detection');
    
    // Listen for clicks on navigation elements (buttons, stepper, etc.)
    document.addEventListener('click', this.handleInternalNavigation.bind(this), true);
    
    // Listen for URL changes (if using React Router or similar)
    this.setupUrlChangeDetection();
    
    // Listen for step changes in the app
    this.setupStepChangeDetection();
  }

  handleInternalNavigation(event) {
    const target = event.target;
    
    // Check if the click is on a navigation element
    const isNavigationElement = this.isNavigationElement(target);
    
    if (isNavigationElement) {
      console.log('🔄 [MOBILE-SCROLL] Internal navigation detected:', {
        element: target.tagName,
        className: target.className,
        text: target.textContent?.trim()
      });
      
      // Activate internal navigation mode
      this.activateInternalNavigationMode();
    }
  }

  isNavigationElement(element) {
    // Check for various navigation elements
    const navigationSelectors = [
      'button', // All buttons
      '.MuiButton-root', // Material-UI buttons
      '.MuiStepLabel-root', // Stepper labels
      '.MuiIconButton-root', // Icon buttons
      '[role="button"]', // Elements with button role
      'a[href]', // Links
      '.step-button', // Custom step buttons
      '.next-button', // Next buttons
      '.back-button', // Back buttons
      '.navigation-button' // Custom navigation buttons
    ];
    
    // Check if element matches any navigation selector
    for (const selector of navigationSelectors) {
      if (element.matches && element.matches(selector)) {
        return true;
      }
    }
    
    // Check if element contains navigation text
    const navigationText = [
      'next', 'back', 'continue', 'proceed', 'step', 'select', 'choose',
      'hybrid', 'power', 'audio', 'dmx', 'style', 'connector', 'length', 'finishing'
    ];
    
    const elementText = element.textContent?.toLowerCase().trim();
    if (elementText) {
      for (const text of navigationText) {
        if (elementText.includes(text)) {
          return true;
        }
      }
    }
    
    return false;
  }

  activateInternalNavigationMode() {
    this.internalNavigationActive = true;
    
    // Clear any existing timeout
    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
    }
    
    // Reset to internal navigation mode after a delay
    this.navigationTimeout = setTimeout(() => {
      this.internalNavigationActive = false;
      console.log('🔄 [MOBILE-SCROLL] Internal navigation mode reset');
    }, 2000); // 2 seconds of internal navigation mode
    
    console.log('🔄 [MOBILE-SCROLL] Internal navigation mode activated');
  }

  setupUrlChangeDetection() {
    // Listen for URL changes (for SPA navigation)
    let currentUrl = window.location.href;
    
    const checkUrlChange = () => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        console.log('🔄 [MOBILE-SCROLL] URL change detected:', currentUrl);
        this.activateInternalNavigationMode();
      }
    };
    
    // Check for URL changes periodically
    setInterval(checkUrlChange, 500);
  }

  setupStepChangeDetection() {
    // Listen for step changes in the app
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          
          // Check if this is a step change (common patterns)
          if (target.classList.contains('MuiStepLabel-root') || 
              target.classList.contains('MuiStepper-root') ||
              target.classList.contains('step-content')) {
            console.log('🔄 [MOBILE-SCROLL] Step change detected');
            this.activateInternalNavigationMode();
          }
        }
      });
    });
    
    // Observe the entire document for changes
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class', 'data-step']
    });
  }

  handleScroll(event) {
    if (!this.isEmbedded || !this.isMobile) return;
    
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDelta = currentScrollY - this.lastScrollY;
    const scrollDirection = scrollDelta > 0 ? 'down' : 'up';
    
    // Update scroll direction
    this.scrollDirection = scrollDirection;
    
    // Throttle scroll events for better performance on iPad
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    this.scrollTimeout = setTimeout(() => {
      // Check if we've reached the bottom or top of the iframe
      const isAtBottom = this.checkIfAtBottom();
      const isAtTop = this.checkIfAtTop();
      
      // If user scrolls to boundaries, immediately override internal navigation mode
      if ((isAtBottom && scrollDirection === 'down') || (isAtTop && scrollDirection === 'up')) {
        if (this.internalNavigationActive) {
          console.log('🔄 [MOBILE-SCROLL] Boundary reached - overriding internal navigation mode');
          this.internalNavigationActive = false;
          if (this.navigationTimeout) {
            clearTimeout(this.navigationTimeout);
            this.navigationTimeout = null;
          }
        }
        
        // Continue scrolling the parent page
        if (this.iframeScrollContinuation) {
          this.continueParentScroll();
        }
      } else if (this.internalNavigationActive) {
        // If internal navigation is active and not at boundaries, stay within iframe
        console.log('🔄 [MOBILE-SCROLL] Internal navigation active - staying within iframe');
        // Just propagate normal scroll, don't continue to parent
        if (Math.abs(scrollDelta) > this.scrollThreshold) {
          this.propagateScrollToParent(currentScrollY);
          this.lastScrollY = currentScrollY;
        }
      } else if (Math.abs(scrollDelta) > this.scrollThreshold) {
        // Normal scroll propagation when not in internal navigation mode
        this.propagateScrollToParent(currentScrollY);
        this.lastScrollY = currentScrollY;
      }
    }, 16); // ~60fps throttling
  }

  handleTouchStart(event) {
    if (!this.isEmbedded || !this.isMobile || !this.isTouchDevice) return;
    
    this.isScrolling = true;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.touchMoved = false;
    
    console.log('👆 [MOBILE-SCROLL] Touch start:', {
      touchY: this.touchStartY,
      timestamp: this.touchStartTime
    });
  }

  handleTouchMove(event) {
    if (!this.isEmbedded || !this.isMobile || !this.isTouchDevice) return;
    
    if (this.isScrolling && this.touchStartY !== undefined) {
      const touchY = event.touches[0].clientY;
      const touchDelta = this.touchStartY - touchY;
      const touchDirection = touchDelta > 0 ? 'down' : 'up';
      
      this.touchMoved = true;
      this.scrollDirection = touchDirection;
      
      // Check if we're at boundaries and should continue scrolling
      const isAtBottom = this.checkIfAtBottom();
      const isAtTop = this.checkIfAtTop();
      
      // If significant touch movement, handle accordingly
      if (Math.abs(touchDelta) > 10) {
        // If user touches to boundaries, immediately override internal navigation mode
        if ((isAtBottom && touchDirection === 'down') || (isAtTop && touchDirection === 'up')) {
          if (this.internalNavigationActive) {
            console.log('🔄 [MOBILE-SCROLL] Touch boundary reached - overriding internal navigation mode');
            this.internalNavigationActive = false;
            if (this.navigationTimeout) {
              clearTimeout(this.navigationTimeout);
              this.navigationTimeout = null;
            }
          }
          
          // Continue parent scroll when at boundaries
          this.continueParentScroll();
        } else if (this.internalNavigationActive) {
          // If internal navigation is active and not at boundaries, stay within iframe
          console.log('🔄 [MOBILE-SCROLL] Internal navigation active - touch staying within iframe');
          // Just propagate normal scroll, don't continue to parent
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
          this.propagateScrollToParent(currentScrollY);
        } else {
          // Normal scroll propagation when not in internal navigation mode
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
          this.propagateScrollToParent(currentScrollY);
        }
      }
    }
  }

  handleTouchEnd(event) {
    if (!this.isEmbedded || !this.isMobile || !this.isTouchDevice) return;
    
    console.log('👆 [MOBILE-SCROLL] Touch end:', {
      touchMoved: this.touchMoved,
      direction: this.scrollDirection
    });
    
    this.isScrolling = false;
    this.touchStartY = undefined;
    this.touchMoved = false;
  }

  handleWheel(event) {
    if (!this.isEmbedded || !this.isMobile) return;
    
    // Propagate wheel events to parent for better scroll control
    this.propagateWheelToParent(event);
  }

  checkIfAtBottom() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight
    );
    
    // Consider "at bottom" if we're within 50px of the bottom
    const isAtBottom = scrollTop + windowHeight >= documentHeight - 50;
    
    console.log('🔍 [MOBILE-SCROLL] Bottom check:', {
      scrollTop,
      windowHeight,
      documentHeight,
      isAtBottom,
      remaining: documentHeight - (scrollTop + windowHeight)
    });
    
    return isAtBottom;
  }

  checkIfAtTop() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Consider "at top" if we're within 50px of the top
    const isAtTop = scrollTop <= 50;
    
    console.log('🔍 [MOBILE-SCROLL] Top check:', {
      scrollTop,
      isAtTop,
      remaining: scrollTop
    });
    
    return isAtTop;
  }

  continueParentScroll() {
    if (!this.iframeScrollContinuation) return;
    
    const isAtBottom = this.checkIfAtBottom();
    const isAtTop = this.checkIfAtTop();
    const location = isAtBottom ? 'bottom' : isAtTop ? 'top' : 'unknown';
    
    console.log(`📱 [MOBILE-SCROLL] Continuing parent scroll - iframe ${location} reached, direction: ${this.scrollDirection}`);
    
    try {
      if (window.parent && window.parent !== window) {
        // Send a special "continue scroll" message to parent
        window.parent.postMessage({
          type: 'nlfx-continue-scroll',
          direction: this.scrollDirection,
          location: location,
          timestamp: Date.now()
        }, '*');
        
        // Also send a wheel event to simulate continued scrolling
        const deltaY = this.scrollDirection === 'down' ? 50 : -50; // Positive for down, negative for up
        window.parent.postMessage({
          type: 'nlfx-wheel',
          deltaY: deltaY,
          deltaX: 0,
          timestamp: Date.now()
        }, '*');
      }
    } catch (error) {
      console.warn('⚠️ [MOBILE-SCROLL] Could not continue parent scroll:', error);
    }
  }

  propagateScrollToParent(scrollY) {
    try {
      // Send scroll position to parent window
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'nlfx-scroll',
          scrollY: scrollY,
          timestamp: Date.now()
        }, '*');
      }
    } catch (error) {
      console.warn('⚠️ [MOBILE-SCROLL] Could not propagate scroll to parent:', error);
    }
  }

  propagateWheelToParent(event) {
    try {
      // Send wheel events to parent for better scroll control
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'nlfx-wheel',
          deltaY: event.deltaY,
          deltaX: event.deltaX,
          timestamp: Date.now()
        }, '*');
      }
    } catch (error) {
      console.warn('⚠️ [MOBILE-SCROLL] Could not propagate wheel to parent:', error);
    }
  }

  addScrollOptimizations() {
    // Add CSS to optimize scrolling behavior for all mobile devices
    const style = document.createElement('style');
    style.textContent = `
      /* Enhanced mobile scroll optimizations for all devices */
      @media (max-width: 1024px) {
        html, body {
          /* Enable smooth scrolling */
          scroll-behavior: smooth !important;
          -webkit-overflow-scrolling: touch !important;
          /* Allow scrolling to propagate */
          overscroll-behavior: contain !important;
          /* Better touch handling for all devices */
          touch-action: pan-y !important;
          /* Prevent zoom on touch */
          -webkit-text-size-adjust: 100% !important;
        }
        
        /* Optimize touch scrolling for all elements */
        * {
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
        }
        
        /* When embedded, lock root to one scroll container inside so iframe touch scroll works */
        ${this.isEmbedded ? `
        #root {
          overflow: hidden !important;
          height: 100% !important;
          min-height: 100vh !important;
        }
        .nlfx-scroll-container {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          min-height: 0 !important;
        }
        ` : `
        #root, .App {
          min-height: 100vh !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
        }
        `}
        
        /* Optimize iframe scrolling */
        iframe {
          overflow: visible !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
        }
        
        /* Android-specific optimizations */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          html, body {
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior: auto !important;
          }
          ${!this.isEmbedded ? `
          #root, .App {
            -webkit-overflow-scrolling: touch !important;
            overflow-y: auto !important;
          }
          ` : ''}
        }
        
        /* iPad-specific optimizations */
        @media (min-width: 768px) and (max-width: 1024px) {
          html, body {
            -webkit-overflow-scrolling: touch !important;
            overscroll-behavior: auto !important;
          }
          ${!this.isEmbedded ? `
          #root, .App {
            -webkit-overflow-scrolling: touch !important;
            overflow-y: auto !important;
          }
          ` : ''}
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  addMobileScrollOptimizations() {
    // Add additional mobile-specific optimizations
    const style = document.createElement('style');
    style.textContent = `
      /* Enhanced mobile scrolling for embedded mode */
      @media (max-width: 768px) {
        /* Don't force overflow:visible on the main scroll container - it must scroll inside the iframe */
        .MuiBox-root:not(.nlfx-scroll-container), .MuiContainer-root:not(.nlfx-scroll-container), .MuiPaper-root {
          overflow: visible !important;
          -webkit-overflow-scrolling: touch !important;
        }
        /* Single scroll container for embedded builder so touch scrolling works inside the iframe */
        .nlfx-scroll-container {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          min-height: 0 !important;
        }
        
        /* Optimize stepper and navigation for mobile */
        .MuiStepper-root {
          overflow: visible !important;
          -webkit-overflow-scrolling: touch !important;
        }
        
        /* Ensure cards and content don't prevent scrolling */
        .MuiCard-root {
          overflow: visible !important;
          -webkit-overflow-scrolling: touch !important;
        }
        
        /* Optimize connector selection modals */
        .MuiDialog-paper {
          max-height: 90vh !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch !important;
        }
        
        /* Ensure touch targets are properly sized */
        .MuiButton-root, .MuiIconButton-root {
          min-height: 44px !important;
          min-width: 44px !important;
          touch-action: manipulation !important;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  // Method to manually trigger parent scroll (useful for programmatic scrolling)
  scrollToParent(scrollY) {
    if (this.isEmbedded && window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({
          type: 'nlfx-scroll-to',
          scrollY: scrollY,
          timestamp: Date.now()
        }, '*');
      } catch (error) {
        console.warn('⚠️ [MOBILE-SCROLL] Could not scroll parent:', error);
      }
    }
  }

  // Method to scroll to a specific element and ensure it's visible
  scrollToElement(element, offset = 0) {
    if (!element) return;
    
    const elementRect = element.getBoundingClientRect();
    const scrollY = window.pageYOffset + elementRect.top - offset;
    
    // Scroll within the iframe
    window.scrollTo({
      top: scrollY,
      behavior: 'smooth'
    });
    
    // Also propagate to parent
    this.scrollToParent(scrollY);
  }

  // Method to enable/disable scroll continuation
  setScrollContinuation(enabled) {
    this.iframeScrollContinuation = enabled;
    console.log(`📱 [MOBILE-SCROLL] Scroll continuation ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Method to manually override internal navigation mode
  overrideInternalNavigation() {
    if (this.internalNavigationActive) {
      console.log('🔄 [MOBILE-SCROLL] Manually overriding internal navigation mode');
      this.internalNavigationActive = false;
      if (this.navigationTimeout) {
        clearTimeout(this.navigationTimeout);
        this.navigationTimeout = null;
      }
    }
  }

  // Method to force internal navigation mode
  forceInternalNavigation(duration = 2000) {
    console.log(`🔄 [MOBILE-SCROLL] Forcing internal navigation mode for ${duration}ms`);
    this.activateInternalNavigationMode();
    
    // Override the timeout duration
    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
    }
    this.navigationTimeout = setTimeout(() => {
      this.internalNavigationActive = false;
      console.log('🔄 [MOBILE-SCROLL] Forced internal navigation mode reset');
    }, duration);
  }

  // Method to get current scroll state
  getScrollState() {
    return {
      isEmbedded: this.isEmbedded,
      isMobile: this.isMobile,
      isTouchDevice: this.isTouchDevice,
      scrollContinuation: this.iframeScrollContinuation,
      scrollDirection: this.scrollDirection,
      atBottom: this.checkIfAtBottom(),
      atTop: this.checkIfAtTop(),
      currentScrollY: window.pageYOffset || document.documentElement.scrollTop,
      internalNavigationActive: this.internalNavigationActive
    };
  }

  // Cleanup method
  destroy() {
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    window.removeEventListener('touchmove', this.handleTouchMove.bind(this));
    window.removeEventListener('wheel', this.handleWheel.bind(this));
    window.removeEventListener('touchstart', this.handleTouchStart.bind(this));
    window.removeEventListener('touchend', this.handleTouchEnd.bind(this));
    document.removeEventListener('click', this.handleInternalNavigation.bind(this), true);
    
    // Clear timeouts
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
    }
  }
}

// Create singleton instance
const mobileScrollService = new MobileScrollService();

export default mobileScrollService;
